/**
 * Passwort zuruecksetzen — der zweite Teil des Ablaufs aus
 * `mail/system-mailer.ts`.
 *
 * Der Link in der Mail zeigt auf die API; Better-Auth prueft dort den Token
 * und leitet mit `?token=…` hierher weiter. Ohne Token gibt es nichts zu tun —
 * dann steht hier nur der Hinweis, den Ablauf neu zu starten.
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';

/** Gleiche Untergrenze wie in `auth/auth.ts` (`minPasswordLength`). */
const MIN_LENGTH = 12;

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    if (password.length < MIN_LENGTH) {
      setFormError(t('reset.too_short', { count: MIN_LENGTH }));
      return;
    }
    if (password !== repeat) {
      setFormError(t('reset.mismatch'));
      return;
    }
    if (!token) return;

    setPending(true);
    try {
      const result = await authClient.resetPassword({ newPassword: password, token });
      if (result.error) {
        setFormError(t('reset.failed'));
        return;
      }
      setDone(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-muted/30 p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('reset.title')}</CardTitle>
          <CardDescription>{token ? t('reset.hint') : t('reset.no_token')}</CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="space-y-4">
              <p className="text-sm">{t('reset.done')}</p>
              <Button className="w-full" onClick={() => void navigate('/login', { replace: true })}>
                {t('reset.to_login')}
              </Button>
            </div>
          ) : token ? (
            <form className="space-y-4" onSubmit={(event) => void submit(event)}>
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('reset.new_password')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="repeat-password">{t('reset.repeat_password')}</Label>
                <Input
                  id="repeat-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={repeat}
                  onChange={(event) => setRepeat(event.target.value)}
                />
              </div>
              {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
              <Button type="submit" className="w-full" disabled={pending}>
                {pending ? t('common.loading') : t('reset.submit')}
              </Button>
            </form>
          ) : (
            <Button className="w-full" onClick={() => void navigate('/login', { replace: true })}>
              {t('reset.to_login')}
            </Button>
          )}
        </CardContent>
      </Card>
      <LanguageSwitcher />
    </div>
  );
}
